import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Search, Star, ShoppingBag, ChevronLeft, ChevronRight, DollarSign, Warehouse, Truck, ShoppingCart } from 'lucide-react';
import { SellerPaymentDialog } from '../components/SellerPaymentDialog';
import { SSCoinIcon } from '../components/SSCoin';
import CategoryMegaMenu from '../components/CategoryMegaMenu';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COIN_RATE = 10;

// Helper function to add product to cart
const addToCart = (product, isRTL) => {
  try {
    const cart = JSON.parse(localStorage.getItem('ss_cart') || '[]');
    const existingIndex = cart.findIndex(item => item.id === product.id);
    
    if (existingIndex >= 0) {
      cart[existingIndex].qty += 1;
      toast.success(isRTL ? `تمت زيادة الكمية: ${cart[existingIndex].qty}` : `Quantity updated: ${cart[existingIndex].qty}`);
    } else {
      cart.push({
        id: product.id,
        name: product.name,
        name_ar: product.name_ar,
        image: product.image,
        buy_price: product.buy_price,
        sell_price: product.sell_price,
        qty: 1
      });
      toast.success(isRTL ? 'تمت الإضافة للسلة!' : 'Added to cart!');
    }
    
    localStorage.setItem('ss_cart', JSON.stringify(cart));
    // Dispatch event to update cart badge
    window.dispatchEvent(new Event('cartUpdated'));
  } catch (e) {
    console.error('Error adding to cart:', e);
    toast.error(isRTL ? 'فشل في الإضافة للسلة' : 'Failed to add to cart');
  }
};

export default function ProductsPage() {
  const { marketplaceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isRTL } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [gender, setGender] = useState('');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [marketplaces, setMarketplaces] = useState([]);
  const [selectedMarketplace, setSelectedMarketplace] = useState(marketplaceId || '');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);
  
  const isPaginationChange = useRef(false);
  const savedScrollPosition = useRef(0);
  const isFilterChange = useRef(false);

  useEffect(() => {
    fetch(`${API_URL}/api/marketplaces`).then(r => r.json()).then(setMarketplaces).catch(console.error);
  }, []);

  useEffect(() => {
    if (!initialized) {
      const urlCategory = searchParams.get('category') || '';
      const urlSubcategory = searchParams.get('subcategory') || '';
      const urlMarketplace = searchParams.get('marketplace_id') || marketplaceId || '';
      const urlSort = searchParams.get('sort') || 'newest';
      const urlSearch = searchParams.get('search') || '';
      const urlPage = parseInt(searchParams.get('page') || '1', 10);

      setCategory(urlCategory);
      setSubcategory(urlSubcategory);
      setSelectedMarketplace(urlMarketplace);
      setSort(urlSort);
      setSearch(urlSearch);
      setPage(urlPage);
      setInitialized(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialized) {
      fetchProducts();
    }
  }, [initialized, selectedMarketplace, category, subcategory, gender, sort, page]);

  useEffect(() => {
    if (initialized) {
      const params = new URLSearchParams();
      if (selectedMarketplace) params.set('marketplace_id', selectedMarketplace);
      if (category) params.set('category', category);
      if (subcategory) params.set('subcategory', subcategory);
      if (gender) params.set('gender', gender);
      if (sort && sort !== 'newest') params.set('sort', sort);
      if (search) params.set('search', search);
      if (page > 1) params.set('page', page.toString());
      
      // Update URL without causing scroll - use native history API
      const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
      window.history.replaceState(null, '', newUrl);
    }
  }, [initialized, selectedMarketplace, category, subcategory, gender, sort, search, page]);

  useEffect(() => {
    if (isPaginationChange.current) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      isPaginationChange.current = false;
    }
  }, [page]);

  // Restore scroll position after filter change and loading completes
  useEffect(() => {
    if (isFilterChange.current && !loading) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        window.scrollTo(0, savedScrollPosition.current);
        isFilterChange.current = false;
      }, 100);
    }
  }, [loading]);

  const fetchProducts = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedMarketplace) params.set('marketplace_id', selectedMarketplace);
    if (category) params.set('category', category);
    if (subcategory) params.set('sub_category', subcategory);
    if (gender) params.set('gender', gender);
    if (sort) params.set('sort', sort);
    if (search) params.set('search', search);
    params.set('page', page); 
    params.set('limit', 40);
    
    try {
      const res = await fetch(`${API_URL}/api/products?${params}`);
      const data = await res.json();
      setProducts(data.products || []); 
      setTotalPages(data.pages || 1); 
      setTotalProducts(data.total || 0);
    } catch(e) { 
      console.error(e); 
    }
    setLoading(false);
  };

  const handleAddToStore = (product) => {
    if (!isAuthenticated) { 
      navigate('/login'); 
      return; 
    }
    setSelectedProduct(product);
    setDialogOpen(true);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchProducts();
  };

  const handlePageChange = (newPage) => {
    isPaginationChange.current = true;
    setPage(newPage);
  };

  const handleCategorySelect = (categoryId, subcatKey, genderVal) => {
    savedScrollPosition.current = window.scrollY;
    isFilterChange.current = true;
    
    setCategory(categoryId);
    setSubcategory(subcatKey || '');
    setGender(genderVal || '');
    setPage(1);
  };

  // Import CATEGORY_STRUCTURE for mobile sidebar
  const CATS = [
    { key:'accessories', icon:'👜', ar:'اكسسوارات', en:'Accessories' },
    { key:'fashion', icon:'👗', ar:'أزياء', en:'Fashion' },
    { key:'beauty', icon:'💄', ar:'جمال وعناية', en:'Beauty' },
    { key:'jewelry', icon:'💎', ar:'مجوهرات', en:'Jewelry' },
    { key:'electronics', icon:'📱', ar:'الكترونيات', en:'Electronics' },
    { key:'home', icon:'🏠', ar:'المنزل', en:'Home & Kitchen' },
    { key:'phones', icon:'📲', ar:'هواتف وأجهزة', en:'Phones & Devices' },
    { key:'sports', icon:'⚽', ar:'رياضة ولياقة', en:'Sports & Fitness' },
    { key:'baby', icon:'👶', ar:'أطفال ورضع', en:'Kids & Baby' },
    { key:'automotive', icon:'🚗', ar:'سيارات', en:'Automotive' },
    { key:'pets', icon:'🐾', ar:'حيوانات أليفة', en:'Pets' },
    { key:'garden', icon:'🌿', ar:'حديقة وهواء طلق', en:'Garden & Outdoor' },
  ];
  const [mobileExpanded, setMobileExpanded] = useState([]);

  return (
    <>
    {/* ===== MOBILE LAYOUT (md:hidden) ===== */}
    <MobileProductsLayout
      isRTL={isRTL}
      search={search}
      setSearch={setSearch}
      handleSearchSubmit={handleSearchSubmit}
      category={category}
      setCategory={setCategory}
      setSubcategory={setSubcategory}
      mobileExpanded={mobileExpanded}
      setMobileExpanded={setMobileExpanded}
      handleCategorySelect={handleCategorySelect}
      CATS={CATS}
      sort={sort}
      setSort={setSort}
      totalProducts={totalProducts}
      loading={loading}
      products={products}
      navigate={navigate}
      page={page}
      totalPages={totalPages}
      handlePageChange={handlePageChange}
      setPage={setPage}
      isAuthenticated={isAuthenticated}
      handleAddToStore={handleAddToStore}
    />

    {/* ===== DESKTOP LAYOUT (hidden md:block) ===== */}
    <div className="hidden md:block space-y-4">
      {/* Compact Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">{isRTL ? 'المنتجات' : 'Products'}</h1>
          <p className="text-sm text-muted-foreground">{isRTL ? 'اختر، أضف لمتجرك، شارك واكسب!' : 'Pick, add to store, share & earn!'}</p>
        </div>
        <Badge variant="secondary" className="font-mono text-xs px-2.5 py-1">{totalProducts.toLocaleString()}+</Badge>
      </div>

      {/* Info Banner - Card Badges */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {[
          { icon: Warehouse, text: isRTL ? 'مخزن في مستودعاتنا' : 'Stored in our warehouses' },
          { icon: Truck, text: isRTL ? 'نحن نشحن للعميل' : 'We ship to customer' },
          { icon: DollarSign, text: isRTL ? 'لا تخزين ولا شحن عليك' : 'No storage fees' },
        ].map((b, i) => (
          <div key={i} className="flex items-center gap-2 flex-shrink-0 px-3 py-2 rounded-xl bg-primary/5 border border-primary/15">
            <b.icon className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="text-xs text-muted-foreground whitespace-nowrap">{b.text}</span>
          </div>
        ))}
      </div>

      {/* Category Mega Menu */}
      <CategoryMegaMenu 
        isRTL={isRTL} 
        onCategorySelect={handleCategorySelect}
        currentCategory={category}
      />

      {/* Search + Sort - Clean single row */}
      <div className="flex gap-2 items-center">
        <form onSubmit={handleSearchSubmit} className="flex-1">
          <div className="relative">
            <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
            <Input 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
              placeholder={isRTL ? 'ابحث عن منتجات...' : 'Search products...'} 
              className={`${isRTL ? 'pr-10' : 'pl-10'} bg-secondary/50 h-10 rounded-xl`} 
              data-testid="product-search-input" 
            />
          </div>
        </form>
        <Select value={sort || 'newest'} onValueChange={setSort}>
          <SelectTrigger className="w-[150px] bg-secondary/50 h-10 rounded-xl" data-testid="sort-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">{isRTL ? 'الأحدث' : 'Newest'}</SelectItem>
            <SelectItem value="price_low">{isRTL ? 'الأرخص' : 'Price Low'}</SelectItem>
            <SelectItem value="price_high">{isRTL ? 'الأغلى' : 'Price High'}</SelectItem>
            <SelectItem value="profit_high">{isRTL ? 'أعلى ربح' : 'Highest Profit'}</SelectItem>
            <SelectItem value="popular">{isRTL ? 'الأكثر مبيعاً' : 'Popular'}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Active Filters Display */}
      {(category || subcategory) && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">{isRTL ? 'الفلاتر النشطة:' : 'Active filters:'}</span>
          {category && (
            <Badge variant="secondary" className="gap-1">
              {category}
              <button onClick={() => { setCategory(''); setSubcategory(''); }} className="ml-1 hover:text-destructive">×</button>
            </Badge>
          )}
          {subcategory && (
            <Badge variant="outline" className="gap-1">
              {subcategory}
              <button onClick={() => setSubcategory('')} className="ml-1 hover:text-destructive">×</button>
            </Badge>
          )}
        </div>
      )}

      {/* Products Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1,2,3,4,5,6,7,8].map(i => <Skeleton key={i} className="h-96 rounded-xl" />)}
        </div>
      ) : products.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">{isRTL ? 'لا توجد منتجات' : 'No products found'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="products-grid">
          {products.map((product, i) => (
            <motion.div 
              key={product.id} 
              initial={{ opacity: 0, y: 10 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ delay: Math.min(i * 0.03, 0.3) }}
            >
              <Card 
                className="bg-card border-border hover:shadow-xl hover:shadow-black/10 transition-all group h-full flex flex-col rounded-lg overflow-hidden" 
                data-testid={`product-card-${product.id}`}
              >
                {/* Image - tall like Amazon */}
                <div className="relative cursor-pointer bg-[#f7f7f7] dark:bg-[#2a2a2a]" onClick={() => navigate(`/product/${product.id}`)}>
                  <img 
                    src={product.image} 
                    alt={isRTL ? product.name_ar : product.name} 
                    className="w-full aspect-square object-contain p-4 group-hover:scale-[1.03] transition-transform duration-300 mix-blend-multiply dark:mix-blend-normal" 
                    loading="lazy" 
                  />
                  {/* Badges */}
                  <div className="absolute top-2 left-2 right-2 flex justify-between items-start">
                    {product.id.charCodeAt(product.id.length - 1) % 3 === 0 ? (
                      <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                        {isRTL ? 'عرض محدود' : 'Limited Deal'}
                      </span>
                    ) : product.id.charCodeAt(product.id.length - 1) % 3 === 1 ? (
                      <span className="bg-amber-500 text-black text-[10px] font-bold px-2 py-0.5 rounded">
                        {isRTL ? 'الأكثر مبيعاً' : 'Best Seller'}
                      </span>
                    ) : (
                      <span />
                    )}
                    <span className="bg-[hsl(var(--success))] text-white text-[10px] font-bold px-2 py-0.5 rounded font-mono">
                      +{product.profit_percent}% {isRTL ? 'ربح' : 'profit'}
                    </span>
                  </div>
                </div>

                {/* Info - Amazon style */}
                <CardContent className="p-3 sm:p-4 flex flex-col flex-1">
                  {/* Product name */}
                  <h3 
                    className="font-medium text-sm mb-1.5 line-clamp-2 flex-1 cursor-pointer hover:text-primary transition-colors leading-snug" 
                    onClick={() => navigate(`/product/${product.id}`)}
                  >
                    {isRTL ? product.name_ar : product.name}
                  </h3>

                  {/* Rating + sold */}
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <div className="flex">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} className={`w-3 h-3 ${s <= Math.round(product.rating) ? 'fill-current text-amber-400' : 'text-gray-300'}`} />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">({product.reviews_count?.toLocaleString()})</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-2">
                    {product.sold_count?.toLocaleString()}+ {isRTL ? 'اشتروه الشهر الماضي' : 'bought in past month'}
                  </p>

                  {/* Price - Amazon style */}
                  <div className="mb-2">
                    {isAuthenticated ? (
                      <div className="space-y-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="flex items-center gap-1 font-mono text-lg font-bold text-amber-400">
                            <SSCoinIcon size={16} />
                            {Math.round(product.buy_price * COIN_RATE).toLocaleString()}
                          </span>
                          <span className="text-[10px] text-muted-foreground px-1.5 py-0.5 bg-emerald-500/10 rounded-full border border-emerald-500/20">
                            ≈ ${product.buy_price} USDT
                          </span>
                        </div>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-[11px] text-muted-foreground">{isRTL ? 'بيع بـ' : 'Sell for'}</span>
                          <span className="font-mono text-sm font-bold text-[hsl(var(--success))]">${product.sell_price}</span>
                          {product.original_price > product.sell_price && (
                            <span className="text-[11px] text-muted-foreground line-through">${product.original_price}</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono text-xl font-bold">${product.sell_price}</span>
                        {product.original_price > product.sell_price && (
                          <span className="text-xs text-muted-foreground line-through">${product.original_price}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Free shipping */}
                  <p className="text-[11px] text-primary font-medium mb-3">
                    {isRTL ? 'شحن مجاني — من مستودعاتنا' : 'FREE shipping — from our warehouse'}
                  </p>

                  {/* Action Buttons - Buy + Cart Icon */}
                  <div className="flex gap-2 mt-auto items-center">
                    <Button 
                      size="sm" 
                      className="flex-1 gap-1.5 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold" 
                      onClick={() => handleAddToStore(product)} 
                      data-testid={`add-to-store-btn-${product.id}`}
                    >
                      <ShoppingBag className="w-3.5 h-3.5" />
                      {isRTL ? 'شراء' : 'Buy'}
                    </Button>
                    {/* Animated Cart Icon */}
                    <button 
                      className="group relative w-10 h-10 rounded-xl border-2 border-primary/30 bg-card flex items-center justify-center transition-all duration-300 hover:border-primary hover:bg-primary/10 hover:scale-110 hover:shadow-lg hover:shadow-primary/20"
                      onClick={(e) => { e.stopPropagation(); addToCart(product, isRTL); }}
                      data-testid={`add-to-cart-btn-${product.id}`}
                    >
                      <ShoppingCart className="w-5 h-5 text-primary transition-transform duration-300 group-hover:scale-125 group-hover:animate-bounce" />
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-opacity" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && !loading && (
        <div className="flex items-center justify-center gap-4">
          <Button 
            variant="outline" 
            size="sm" 
            disabled={page <= 1} 
            onClick={() => handlePageChange(page - 1)}
            data-testid="products-pagination-prev"
          >
            {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
          <span className="text-sm text-muted-foreground font-mono">{page} / {totalPages}</span>
          <Button 
            variant="outline" 
            size="sm" 
            disabled={page >= totalPages} 
            onClick={() => handlePageChange(page + 1)}
            data-testid="products-pagination-next"
          >
            {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
        </div>
      )}

    </div>{/* End Desktop Layout */}

    {/* Seller Payment Dialog - shared between mobile/desktop */}
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        {selectedProduct && (
          <SellerPaymentDialog
            product={selectedProduct}
            userId={user?._id}
            userCoinBalance={user?.coin_balance || 0}
            isRTL={isRTL}
            onSuccess={() => { setDialogOpen(false); navigate('/my-store'); toast.success(isRTL ? 'تم إضافة المنتج لمتجرك!' : 'Product added!'); }}
            onCancel={() => setDialogOpen(false)}
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}

// Mobile split layout with scroll-pass-through

// Mobile split layout - simple CSS-based scroll pass-through
function MobileProductsLayout({ isRTL, search, setSearch, handleSearchSubmit, category, setCategory, setSubcategory, mobileExpanded, setMobileExpanded, handleCategorySelect, CATS, sort, setSort, totalProducts, loading, products, navigate, page, totalPages, handlePageChange, setPage, isAuthenticated, handleAddToStore }) {
  return (
    <div className="md:hidden -m-4 sm:-m-6">
      {/* Search Bar */}
      <div className="flex items-center gap-2 p-3">
        <form onSubmit={handleSearchSubmit} className="flex-1">
          <div className="relative">
            <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={isRTL ? 'بحث...' : 'Search...'} className={`${isRTL ? 'pr-9' : 'pl-9'} h-9 text-sm bg-secondary/50 rounded-xl`} />
          </div>
        </form>
      </div>

      {/* Split View */}
      <div className="flex" style={{height:'calc(100vh - 115px)'}}>
        {/* Categories */}
        <div className={`w-[35%] flex-shrink-0 overflow-y-auto bg-muted/20 ${isRTL ? 'order-1 border-l border-border' : 'order-2 border-r border-border'}`}
          style={{WebkitOverflowScrolling:'touch', overscrollBehavior:'auto'}}>
          <button onClick={() => { setCategory(''); setSubcategory(''); setMobileExpanded([]); setPage(1); }}
            className={`w-full px-2 py-2.5 text-[11px] font-medium border-b border-border/50 transition-colors ${!category ? 'bg-primary/10 text-primary font-bold' : 'text-foreground hover:bg-secondary/50'}`}>
            {isRTL ? 'كل المنتجات' : 'All'}
          </button>
          {CATS.map(cat => (
            <div key={cat.key}>
              <button onClick={() => { setMobileExpanded(prev => prev.includes(cat.key) ? prev.filter(k => k !== cat.key) : [...prev, cat.key]); handleCategorySelect(cat.key, '', ''); }}
                className={`w-full px-2 py-2.5 text-[11px] font-medium border-b border-border/50 flex items-center gap-1.5 transition-colors ${category === cat.key ? 'bg-primary/10 text-primary font-bold' : 'text-foreground hover:bg-secondary/50'}`}>
                <span className="text-sm">{cat.icon}</span>
                <span className="flex-1 text-right">{isRTL ? cat.ar : cat.en}</span>
              </button>
              {mobileExpanded.includes(cat.key) && (
                <div className="bg-secondary/30">
                  <CategoryMegaMenu isRTL={isRTL} onCategorySelect={handleCategorySelect} currentCategory={category} layout="mobile-sub" parentKey={cat.key} />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Products */}
        <div className={`w-[65%] overflow-y-auto px-2 pb-4 ${isRTL ? 'order-2' : 'order-1'}`}
          style={{WebkitOverflowScrolling:'touch', overscrollBehavior:'auto'}}>
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-[10px] text-muted-foreground">{totalProducts} {isRTL ? 'منتج' : 'items'}</span>
            <Select value={sort || "newest"} onValueChange={setSort}>
              <SelectTrigger className="w-[100px] h-7 text-[10px] bg-secondary/50"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{isRTL ? 'الأحدث' : 'Newest'}</SelectItem>
                <SelectItem value="price_low">{isRTL ? 'الأرخص' : 'Low'}</SelectItem>
                <SelectItem value="price_high">{isRTL ? 'الأغلى' : 'High'}</SelectItem>
                <SelectItem value="profit_high">{isRTL ? 'أعلى ربح' : 'Profit'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 gap-2 px-1">{[1,2,3,4].map(i => <Skeleton key={i} className="h-44 rounded-xl" />)}</div>
          ) : (
            <div className="grid grid-cols-2 gap-2 px-1">
              {products.map((product, i) => (
                <Card key={product.id || i} className="overflow-hidden hover:shadow-md transition-shadow rounded-xl">
                  <div className="relative cursor-pointer" onClick={() => navigate(`/product/${product.id}`)}>
                    <img src={product.image} alt="" className="w-full aspect-square object-contain bg-white p-1" loading="lazy" />
                    {product.profit_percent > 50 && <Badge className="absolute top-1 left-1 bg-[hsl(var(--success))] text-white border-0 text-[8px] px-1">+{product.profit_percent}%</Badge>}
                    {product.original_price > product.sell_price && <Badge className="absolute top-1 right-1 bg-red-500 text-white border-0 text-[8px] px-1">-{Math.round((1 - product.sell_price / product.original_price) * 100)}%</Badge>}
                  </div>
                  <CardContent className="p-2">
                    <p className="text-[10px] font-medium line-clamp-1 mb-1 cursor-pointer" onClick={() => navigate(`/product/${product.id}`)}>{isRTL ? product.name_ar : product.name}</p>
                    {isAuthenticated ? (
                      <div className="mb-1.5">
                        <div className="flex items-center gap-1">
                          <SSCoinIcon size={12} />
                          <span className="font-bold text-xs text-amber-400" dir="ltr">{Math.round(product.buy_price * COIN_RATE)}</span>
                          <span className="text-[8px] text-emerald-500">≈${product.buy_price}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 mb-1.5">
                        <span className="font-bold text-xs" dir="ltr">${product.sell_price}</span>
                        {product.original_price > product.sell_price && <span className="text-[8px] text-muted-foreground line-through" dir="ltr">${product.original_price}</span>}
                      </div>
                    )}
                    {/* Buy + Cart buttons */}
                    <div className="flex gap-1.5 items-center">
                      <Button 
                        size="sm" 
                        className="flex-1 h-7 text-[10px] gap-1 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold" 
                        onClick={(e) => { e.stopPropagation(); handleAddToStore(product); }}
                        data-testid={`mobile-buy-${product.id}`}
                      >
                        {isRTL ? 'شراء' : 'Buy'}
                      </Button>
                      <button 
                        className="group w-7 h-7 rounded-lg border border-primary/30 flex items-center justify-center transition-all hover:bg-primary/10 hover:scale-110 active:scale-95"
                        onClick={(e) => { e.stopPropagation(); addToCart(product, isRTL); }}
                        data-testid={`mobile-cart-${product.id}`}
                      >
                        <ShoppingCart className="w-3.5 h-3.5 text-primary group-hover:animate-bounce" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {totalPages > 1 && !loading && (
            <div className="flex items-center justify-center gap-3 py-4">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => handlePageChange(page - 1)} className="h-8 w-8 p-0">
                {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
              </Button>
              <span className="text-sm font-mono">{page}/{totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)} className="h-8 w-8 p-0">
                {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

